import LoadingBackdrop from '../components/LoadingBackdrop'
import React, {useEffect, useState} from 'react'
import AccountApi from '../logic/AccountApi'
import {Link, useSearchParams} from 'react-router-dom'
import {Alert, Box, Grid, TextField} from '@mui/material'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import ApiException from '../model/ApiException'

const api = new AccountApi()

export default function ResetPassword() {
  const [loading, setLoading] = useState<boolean>(true)
  const [hasCodeError, setHasCodeError] = useState<boolean>(false)
  const [password, setPassword] = useState<string>('')
  const [confirmedPassword, setConfirmedPassword] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code') ?? ''
  const genericErrorMessage = 'Something went wrong - please try again later'

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    if (loading) return

    if (!password) {
      setError('Please enter a new password')
      return
    }

    if (password !== confirmedPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const res = await api.changePassword(password, code)

      if (res.isError()) {
        const ex = res.exception as ApiException

        if (res.statusCode === 400 && !!ex.errors['Password']) {
          setError(ex.errors['Password'][0])
        } else {
          setError(genericErrorMessage)
        }

        return
      }

      setSuccess(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!code) {
      return
    }
    api.verifyResetToken(code)
      .then(res => {
        if (res.isError()) {
          setHasCodeError(true)
          return
        }
      })
      .catch(() => {
        setHasCodeError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  return (
    <>
      <LoadingBackdrop loading={loading}/>
      <h1>Reset Password</h1>
      {loading && <p>Loading...</p>}
      {!loading && hasCodeError && <p>{genericErrorMessage}</p>}
      {!success && !loading && !hasCodeError && <Box component="form" noValidate onSubmit={handleSubmit} sx={{mt: 3}}>
        <Grid item xs={12}>
          <TextField
            value={password}
            onChange={e => {
              setError('');
              setPassword(e.target.value)
            }}
            required
            fullWidth
            name="password"
            label="New Password"
            type="password"
            id="password"
            error={!!error}
          />
        </Grid>
        <Grid item xs={12} sx={{mt: '15px'}}>
          <TextField
            value={confirmedPassword}
            onChange={e => {
              setError('');
              setConfirmedPassword(e.target.value)
            }}
            required
            fullWidth
            name="confirmedPassword"
            label="Confirm Password"
            type="password"
            id="confirmedPassword"
            error={!!error}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{mt: 3, mb: 2}}
          >
            Change Password
          </Button>
        </Grid>
        {!!error && <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>}
      </Box>}
      {success && <Box>
        Password changed successfully. <MuiLink to="/login" component={Link} style={{textDecoration: "none"}}>Go to login</MuiLink>
      </Box>}
    </>
  )
}
